<template>
    <Select :modelValue="modelValue" :options="options" optionLabel="label" optionValue="value"
        :placeholder="placeholder || 'Select Course'" :class="className" filter showClear @update:modelValue="onUpdate" @change="onChange"
        @filter="onFilter" :loading="searching" :size="size || undefined"
        data-testid="select-course">
        <template #option="{ option }">
            <div class="flex flex-col py-0.5">
                <span class="text-sm font-medium">{{ option.code }} — {{ option.title }}</span>
                <span class="text-xs text-gray-400">Unit: {{ option.unit }} · {{ option.instance || '—' }}</span>
            </div>
        </template>
    </Select>
</template>

<script setup lang="ts">
import { useCourses } from '@/composables/queries/useCourses';
import { formatCourseInstance } from '@/utils/courseInstance';
import type { Course } from '@/types/composition-functions';

const props = defineProps({
  modelValue: {
    type: [String, Number, Object],
    default: null,
  },
  placeholder: {
    type: String,
    default: '',
  },
  className: {
    type: String,
    default: 'w-full',
  },
  size: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const { courses, loadingSchoolCourses: searching, setFilter } = useCourses({ autoFetch: true, first: 100, page: 1 });

type CourseOptionSource = Pick<Course, 'id' | 'course_code' | 'course_title' | 'unit' | 'course_instance_id'>
  & { course_instance_year?: number | null };

const options = computed(() => courses.value.map((s: CourseOptionSource) => ({
  label: `${s.course_code} — ${s.course_title} (${s.unit} units)`,
  value: s.id,
  code: s.course_code,
  title: s.course_title,
  unit: s.unit,
  instance: formatCourseInstance(s),
})));

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
function onFilter(event: { originalEvent: Event; value: string }) {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    setFilter({ query: event.value });
  }, 300);
}

const onUpdate = (value: string | number | null) => {
  emit('update:modelValue', value);
};

const onChange = (event: Event) => {
  emit('change', event);
};
</script>
